import React, { useMemo } from "react";

import displayToast from "@common/components/Toast/displayToast";
import { ensureUTC } from "@common/models/helpers/time/timeSince";
import { setUnreadChangeLogNotifications } from "@main/context/App/App.actions";
import { useAppContext, useAppDispatch } from "@main/context/App/App.context";
import { FeaturesService } from "@main/pages/Info/services/Features.service";
import { useQuery } from "@tanstack/react-query";

import { InterjectedRowType, PromoRowItem } from "./InterjectedRow.model";
import * as Styled from "./InterjectedRows.styles";

const featuresService = new FeaturesService();

/// Finds where a release sits among tickets sorted newest-first: the row goes right after the last
/// ticket that's still newer than the release (so newer tickets stay above it, older ones below).
function findReleaseDatePosition(ticketDates: string[], releaseDateMs: number): number {
  const boundaryIndex = ticketDates.findIndex((date) => new Date(ensureUTC(date)).getTime() <= releaseDateMs);

  /// Every loaded ticket is newer than the release - best-effort until more rows load.
  if (boundaryIndex === -1) return Math.max(ticketDates.length - 1, 0);

  return Math.max(boundaryIndex - 1, 0);
}

function parseReleaseDate(date?: string): number | undefined {
  if (!date) return undefined;

  const ms = new Date(`${date}T00:00:00Z`).getTime();
  return Number.isNaN(ms) ? undefined : ms;
}

/**
 * The "what's new" interjected rows - one per changelog entry the current user hasn't seen yet,
 * each positioned at its own release date among the tickets, sourced from the changelog API rather
 * than any hardcoded promo content.
 *
 * Unlike the ticket-cadence rows, read/unread state here is already tracked server-side per user
 * (via ChangelogNotification), so there's nothing new to persist locally for that part.
 */
export function useWhatsNewInterjection(ticketDates: string[]) {
  const { unreadChangeLogNotifications } = useAppContext();
  const appDispatch = useAppDispatch();

  const { data: latestChangeLogs } = useQuery(
    ["latest change logs"],
    async () => (await featuresService.getChangeLogs("latest")).data,
    { refetchOnWindowFocus: false, cacheTime: Infinity },
  );

  /// Newest first (the changelogs come sorted "latest"), so rows that land on the same ticket keep
  /// that order. Entries without a valid release_date are skipped - there's nothing to position them by.
  const matches = useMemo(() => {
    if (!latestChangeLogs?.length || !unreadChangeLogNotifications.length) return [];

    return latestChangeLogs.flatMap((changelog) => {
      const releaseDateMs = parseReleaseDate(changelog.release_date);
      if (releaseDateMs === undefined) return [];

      const notification = unreadChangeLogNotifications.find((n) => n.changelog_id === changelog.id && !n.is_read);
      return notification ? [{ changelog, notification, releaseDateMs, rowId: `whats-new-${changelog.id}` }] : [];
    });
  }, [latestChangeLogs, unreadChangeLogNotifications]);

  const rows: PromoRowItem[] = useMemo(
    () =>
      matches.map(({ changelog, releaseDateMs, rowId }) => {
        const firstLink = changelog.links?.[0];

        return {
          id: rowId,
          type: InterjectedRowType.PROMO,
          after_index: findReleaseDatePosition(ticketDates, releaseDateMs),
          icon: changelog.image_url ? (
            <Styled.Icon
              src={changelog.image_url}
              alt=""
            />
          ) : undefined,
          title: changelog.title,
          description: changelog.description,
          action_label: firstLink?.title,
          action_url: firstLink?.url,
        };
      }),
    [matches, ticketDates],
  );

  /// No-op for ids that aren't what's-new rows, so callers can pass any dismissed row id.
  const markAsRead = (rowId: string) => {
    const match = matches.find((candidate) => candidate.rowId === rowId);
    if (!match) return;

    featuresService
      .markNotificationAsRead(match.changelog.id)
      .then(async () => {
        const { data } = await featuresService.getChangeLogsNotifications();
        appDispatch(setUnreadChangeLogNotifications(data));
      })
      .catch(() => {
        displayToast({ title: "Something went wrong", content: "Please try again.", duration: 2000 });
      });
  };

  return { rows, markAsRead };
}
